import React, { RefObject, useContext, useState } from 'react';
import { Col, Container, Image, Nav, Navbar, NavDropdown, Offcanvas, Row } from 'react-bootstrap';
import { Link, useLocation } from 'react-router-dom';
import LogoFullImgSrc from '../../../../assets/images/sendit-logo-full-color.svg';
import LogoMarkImgSrc from '../../../../assets/images/sendit-logo-mark-color.svg';
import { UserTypeEnum } from '../../../api';
import { SessionContext } from '../../contexts/SessionContext';
import { useUserProgress } from '../../hooks/user-progress';
import { Avatar } from '../Avatar';

import { NavBarConnectionRequestsBadge, NavBarPlacementRequestsBadge } from '../NotificationBadge';
import './branded-navbar.scss';

const BrandedNavbarLink = ({
  to,
  children,
  onLinkClick,
}: {
  to: string;
  children: React.ReactNode;
  onLinkClick?: () => void;
}) => {
  const location = useLocation();
  const pathnameWithoutTrailingSlash = location.pathname.replace(/\/$/, '');

  const isActive =
    to === '/' ? pathnameWithoutTrailingSlash === '' : pathnameWithoutTrailingSlash.startsWith(to);

  const handleClick = () => {
    if (onLinkClick) {
      onLinkClick();
    }
  };

  return (
    <Nav.Link active={isActive} as={Link} to={to} onClick={handleClick}>
      {children}
    </Nav.Link>
  );
};

export const BrandedNavbar = ({ navbarRef }: { navbarRef?: RefObject<HTMLElement> }) => {
  const { user, logout } = useContext(SessionContext);
  const userIsProvider = user?.user_type == UserTypeEnum.PROVIDER;
  const { isProfileComplete } = useUserProgress(user?.provider_completed_registration_steps);
  const location = useLocation();
  const isAppPage =
    location.pathname.startsWith('/app/healthcare-entity') ||
    location.pathname.startsWith('/app/organization') ||
    location.pathname.startsWith('/app/provider');
  const isLoginOrRegisterPage =
    location.pathname.startsWith('/login') ||
    location.pathname.startsWith('/register') ||
    location.pathname.startsWith('/verify-email') ||
    location.pathname.startsWith('/password-reset') ||
    location.pathname.startsWith('/forgot-password') ||
    location.pathname.startsWith('/provider-reference-survey') ||
    location.pathname.startsWith('/app/provider/onboarding');

  const [showBrandedNavBar, setShowBrandedNavBar] = useState(false);

  if (isLoginOrRegisterPage) {
    return null;
  }

  let homeUrl: string;
  if (isAppPage && user?.user_type === UserTypeEnum.PROVIDER) {
    homeUrl = '/app/provider';
  } else if (isAppPage && user?.user_type === UserTypeEnum.HEALTHCARE_ENTITY_REP) {
    homeUrl = '/app/healthcare-entity';
  } else {
    homeUrl = 'https://www.senditgigs.com/';
  }

  const handleToggle = () => setShowBrandedNavBar((prev) => !prev);
  const handleClose = () => setShowBrandedNavBar(false);
  return (
    <Navbar className="branded-navbar" bg="light" expand="lg" ref={navbarRef}>
      <Container>
        <Nav.Link as={Link} to={homeUrl}>
          <Navbar.Brand>
            <Image alt="Sendit logo" src={LogoFullImgSrc.toString()} className="navbar-logo" />
          </Navbar.Brand>
        </Nav.Link>
        <div className="d-flex align-items-center">
          {/* TODO: shouldn't have to be so explicit with styles here, find the collisions in the custom css */}
          <Nav.Link
            as={Link}
            to={homeUrl}
            className="d-lg-none"
            style={{
              width: '24px',
              height: '24px',
              marginBottom: '5px',
              color: 'rgba(0,0,0,0.65)',
            }}
          >
            <i className="far fa-fw fa-lg fa-house me-3 mb-1 " />
          </Nav.Link>
          <Navbar.Toggle aria-controls="responsive-navbar-nav" onClick={handleToggle} />
        </div>
        <Navbar.Offcanvas
          id={`offcanvasNavbar-expand`}
          aria-labelledby={`offcanvasNavbarLabel-expand`}
          placement="end"
          show={showBrandedNavBar}
          onHide={handleClose}
        >
          <Offcanvas.Header closeButton>
            <Offcanvas.Title>
              <Image alt="Sendit logo" src={LogoMarkImgSrc.toString()} className="logo-mark" />
            </Offcanvas.Title>
          </Offcanvas.Header>
          <Offcanvas.Body className="branded-navbar-offcanvas">
            <Nav className="me-auto flex-grow-1">
              {!isAppPage && (
                <BrandedNavbarLink to="https://www.senditgigs.com/" onLinkClick={handleClose}>
                  Home
                </BrandedNavbarLink>
              )}
              {userIsProvider && isAppPage && (
                <>
                  <BrandedNavbarLink to="/app/provider/home" onLinkClick={handleClose}>
                    <div className="d-flex align-items-center">
                      <i className="fal fa-fw fa-lg fa-house me-2" />
                      <span className="nav-link-text">Home</span>
                    </div>
                  </BrandedNavbarLink>
                  <BrandedNavbarLink to="/app/provider/profile" onLinkClick={handleClose}>
                    <div className="d-flex align-items-center">
                      <i className="fal fa-fw fa-lg fa-address-card me-2" />
                      <span className="nav-link-text">Profile</span>
                    </div>
                  </BrandedNavbarLink>
                  <BrandedNavbarLink
                    to="/app/provider/connection-requests"
                    onLinkClick={handleClose}
                  >
                    <div className="d-flex align-items-center">
                      <i className="fal fa-fw fa-lg fa-handshake me-2" />
                      <span className="nav-link-text">Connections</span>
                      <NavBarConnectionRequestsBadge />
                    </div>
                  </BrandedNavbarLink>
                  <BrandedNavbarLink to="/app/provider/booking-requests" onLinkClick={handleClose}>
                    <div className="d-flex align-items-center">
                      <i className="fal fa-fw fa-lg fa-calendar-lines-pen me-2" />
                      <span className="nav-link-text">Assignments</span>
                      <NavBarPlacementRequestsBadge />
                    </div>
                  </BrandedNavbarLink>
                </>
              )}
              {!userIsProvider && isAppPage && (
                <>
                  <BrandedNavbarLink
                    to="/app/healthcare-entity/providers"
                    onLinkClick={handleClose}
                  >
                    <div className="d-flex align-items-center">
                      <i className="fal fa-fw fa-lg fa-stethoscope me-2" />
                      <span className="nav-link-text">Providers</span>
                    </div>
                  </BrandedNavbarLink>

                  {(user?.permissions?.includes('read__assignments__opportunity') ||
                    user?.permissions?.includes('read__staffing__need')) && (
                    <NavDropdown
                      title={
                        <span className="d-flex align-items-center">
                          <i className="fal fa-fw fa-lg fa-id-card me-2" />
                          <span className="nav-link-text">Staffing</span>
                        </span>
                      }
                    >
                      {user?.permissions?.includes('read__staffing__need') && (
                        <NavDropdown.Item
                          className="dropdowm-item-center"
                          as={Link}
                          to="/app/organization/needs"
                          onClick={handleClose}
                        >
                          <div className="d-flex align-items-center">
                            <i className="far fa-fw fa-lg fa-users-medical me-2" />
                            <span className="nav-link-text">Needs</span>
                          </div>
                        </NavDropdown.Item>
                      )}
                      {user?.permissions?.includes('read__assignments__opportunity') && (
                        <NavDropdown.Item
                          className="dropdowm-item-center"
                          as={Link}
                          to="/app/organization/opportunities"
                          onClick={handleClose}
                        >
                          <div className="d-flex align-items-center">
                            <i className="fal fa-fw fa-lg fa-suitcase-medical me-2" />
                            <span className="nav-link-text">Opportunities</span>
                          </div>
                        </NavDropdown.Item>
                      )}
                    </NavDropdown>
                  )}

                  {user?.group_names?.includes('connect_with_providers') && (
                    <BrandedNavbarLink
                      to="/app/healthcare-entity/connection-requests"
                      onLinkClick={handleClose}
                    >
                      <div className="d-flex align-items-center">
                        <i className="fal fa-fw fa-lg fa-handshake me-2" />
                        <span className="nav-link-text">Connections</span>
                        <NavBarConnectionRequestsBadge />
                      </div>
                    </BrandedNavbarLink>
                  )}
                  {user?.group_names?.includes('schedule_shifts') && (
                    <BrandedNavbarLink
                      to="/app/healthcare-entity/booking-requests"
                      onLinkClick={handleClose}
                    >
                      <div className="d-flex align-items-center">
                        <i className="fal fa-fw fa-lg fa-calendar-lines-pen me-2" />
                        <span className="nav-link-text">Assignments</span>
                        <NavBarPlacementRequestsBadge />
                      </div>
                    </BrandedNavbarLink>
                  )}
                </>
              )}
            </Nav>

            <Nav>
              {isAppPage && (
                <NavDropdown
                  className="dropdown-toggle-center"
                  title={
                    <span className="d-inline-flex align-items-center gap-1">
                      <Avatar size={40} className="me-2" image={user?.profile_picture} />
                      {user ? (
                        <Row className="flex-column justify-content-center">
                          <Col>
                            <span className="nav-bar--user-name">{`${user.first_name} ${user.last_name}`}</span>
                          </Col>
                          {user?.healthcare_entity_name && (
                            <span className="small text-muted">{user.healthcare_entity_name}</span>
                          )}
                        </Row>
                      ) : (
                        <div className="profile-name-skeleton" />
                      )}
                    </span>
                  }
                >
                  {user?.user_type === UserTypeEnum.PROVIDER && (
                    <>
                      <NavDropdown.Item
                        className="dropdowm-item-center"
                        as={Link}
                        to="/app/provider/profile"
                        onClick={handleClose}
                      >
                        <i className="fal fa-fw fa-lg fa-address-card me-2" /> Profile
                      </NavDropdown.Item>
                      <NavDropdown.Item
                        className="dropdowm-item-center"
                        as={Link}
                        to="/app/provider/documents"
                        onClick={handleClose}
                      >
                        <i className="fal fa-fw fa-lg fa-files me-2" /> Documents
                      </NavDropdown.Item>
                    </>
                  )}
                  {user?.user_type === UserTypeEnum.HEALTHCARE_ENTITY_REP && (
                    <>
                      <NavDropdown.Item
                        className="dropdowm-item-center"
                        as={Link}
                        to="/app/healthcare-entity/facilities"
                        onClick={handleClose}
                      >
                        <div className="d-flex align-items-center">
                          <i className="fal fa-fw fa-lg fa-hospital me-2" />
                          <span>Facilities</span>
                        </div>
                      </NavDropdown.Item>
                      {user?.group_names?.includes('manage_users') && (
                        <NavDropdown.Item
                          className="dropdowm-item-center"
                          as={Link}
                          to="/app/healthcare-entity/users"
                          onClick={handleClose}
                        >
                          <div className="d-flex align-items-center">
                            <i className="fal fa-fw fa-lg fa-id-badge me-2" />
                            <span>Team</span>
                          </div>
                        </NavDropdown.Item>
                      )}
                    </>
                  )}
                  <NavDropdown.Divider />
                  <NavDropdown.Item
                    className="dropdowm-item-center"
                    onClick={() => {
                      logout?.();
                      handleClose();
                    }}
                  >
                    <div className="d-flex align-items-center">
                      <i className="fal fa-fw fa-lg fa-arrow-right-from-bracket me-2" />
                      <span>Sign out</span>
                    </div>
                  </NavDropdown.Item>
                </NavDropdown>
              )}
              {!isAppPage && (
                <BrandedNavbarLink to="/login" onLinkClick={handleClose}>
                  Login
                </BrandedNavbarLink>
              )}
            </Nav>
          </Offcanvas.Body>
        </Navbar.Offcanvas>
      </Container>
    </Navbar>
  );
};
